import React from "react";
import { motion } from "framer-motion";
import {
  Globe,
  Smartphone,
  Palette,
  Megaphone,
  BarChart3,
  Search,
  Cpu,
  Server,
  Wrench,
  Handshake,
} from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const SERVICES_LIST = [
  { key: "web", icon: Globe, title: "Websites That Sell.", desc: "Fast. Modern. Built to convert." },
  { key: "apps", icon: Smartphone, title: "Apps People Love.", desc: "Smooth experiences that keep users coming back." },
  { key: "brand", icon: Palette, title: "Brands That Stay.", desc: "Logos. Identity. Positioning. Recognition." },
  { key: "ads", icon: Megaphone, title: "Ads That Get Clicked.", desc: "Creative campaigns people actually remember." },
  { key: "growth", icon: BarChart3, title: "Marketing That Moves Numbers.", desc: "More reach. More leads. More customers." },
  { key: "sales", icon: Handshake, title: "Sales That Never Sleep.", desc: "B2B. B2C. Growth that keeps moving." },
  { key: "seo", icon: Search, title: "SEO That Wins.", desc: "Found faster. Rank higher. Grow longer." },
  { key: "ai", icon: Cpu, title: "AI That Saves Time.", desc: "Automate repetitive work. Focus on growing." },
  { key: "host", icon: Server, title: "Infrastructure That Never Stops.", desc: "Fast. Secure. Always online." },
  { key: "maint", icon: Wrench, title: "Support That Actually Supports.", desc: "No tickets. No waiting. Just real people." },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="relative bg-[#08080a] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-14 flex flex-col items-start justify-between gap-6 lg:mb-20 lg:flex-row lg:items-end">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">
                02 · Capabilities
              </span>
            </div>
            <h2 className="font-display text-4xl leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Everything you need.<br />
              <span className="silver-gradient-text">Nothing you don&apos;t.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-zinc-400">
            Ten integrated services. Senior specialists. No agency roulette.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {SERVICES_LIST.map((s, idx) => (
            <motion.div
              key={s.key}
              data-testid={`${SERVICES.cardPrefix}-${s.key}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.55,
                delay: (idx % 5) * 0.06,
                ease: [0.2, 0.8, 0.2, 1],
              }}
              className="group relative rounded-2xl border border-white/[0.06] bg-[#101012] p-6 transition-all duration-500 hover:border-[var(--gold)]/25 hover:bg-[#141416]"
            >
              <div className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-zinc-300 transition-colors duration-500 group-hover:border-[var(--gold)]/40 group-hover:bg-[var(--gold)]/10 group-hover:text-[var(--gold-bright)]">
                <s.icon className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <h3 className="mt-5 font-display text-[17px] font-semibold tracking-tight text-white">
                {s.title}
              </h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-zinc-400">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
